import { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import RecordTable from "components/RecordTable";

import { adminApi } from "services/api";
import PaymentLinkModal from "components/PaymentLinkModal";
import CallButton from "components/CallButton";
import { contactSubmissionsToRows } from "utils/exportUtils";

const STATUS_FILTER_OPTIONS = ["NEW", "READ", "RESPONDED", "ARCHIVED"].map((s) => ({
  value: s,
  label: s.charAt(0) + s.slice(1).toLowerCase(),
}));

const EXPORT_CONFIG = {
  fetchAll: adminApi.exportContactSubmissions,
  toRows: contactSubmissionsToRows,
  filename: "contact-submissions",
  title: "Contact Submissions",
};

function ContactSubmissions() {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [paymentLinkTarget, setPaymentLinkTarget] = useState(null);

  const handleViewDetails = (submission) => {
    setSelectedSubmission(submission);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedSubmission(null);
  };

  const columns = [
    { Header: "name", accessor: "name", width: "20%", align: "left" },
    { Header: "email", accessor: "email", width: "25%", align: "left" },
    { Header: "subject", accessor: "subject", width: "25%", align: "left" },
    { Header: "date", accessor: "date", width: "20%", align: "left" },
    { Header: "actions", accessor: "actions", width: "10%", align: "center" },
  ];

  const renderRow = (submission) => ({
    name: submission.name,
    email: submission.email,
    subject: submission.subject || "No Subject",
    date: new Date(submission.createdAt).toLocaleDateString(),
    actions: (
      <MDBox display="flex" justifyContent="center">
        <Tooltip title="View details">
          <IconButton onClick={() => handleViewDetails(submission)}>
            <Icon>visibility</Icon>
          </IconButton>
        </Tooltip>
        <Tooltip title="Create payment link">
          <IconButton onClick={() => setPaymentLinkTarget(submission)} sx={{ color: "#00996a" }}>
            <Icon>link</Icon>
          </IconButton>
        </Tooltip>
      </MDBox>
    ),
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Contact Form Submissions
                </MDTypography>
              </MDBox>

              <RecordTable
                columns={columns}
                fetchPage={adminApi.getContactSubmissions}
                renderRow={renderRow}
                statusOptions={STATUS_FILTER_OPTIONS}
                exportConfig={EXPORT_CONFIG}
                emptyMessage="No submissions yet"
              />
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isPhone}
      >
        <DialogTitle>Contact Submission Details</DialogTitle>
        <DialogContent>
          {selectedSubmission && (
            <>
              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Name:
                </MDTypography>
                <MDTypography variant="body2">{selectedSubmission.name}</MDTypography>
              </MDBox>
              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Email:
                </MDTypography>
                <MDTypography variant="body2">{selectedSubmission.email}</MDTypography>
              </MDBox>
              {selectedSubmission.phone && (
                <MDBox mb={2}>
                  <MDTypography variant="button" fontWeight="bold">
                    Phone:
                  </MDTypography>
                  <MDBox display="flex" alignItems="center" gap={0.5}>
                    <MDTypography variant="body2">{selectedSubmission.phone}</MDTypography>
                    <CallButton phoneNumber={selectedSubmission.phone} />
                  </MDBox>
                </MDBox>
              )}
              {selectedSubmission.subject && (
                <MDBox mb={2}>
                  <MDTypography variant="button" fontWeight="bold">
                    Subject:
                  </MDTypography>
                  <MDTypography variant="body2">{selectedSubmission.subject}</MDTypography>
                </MDBox>
              )}
              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Message:
                </MDTypography>
                <MDTypography variant="body2">{selectedSubmission.message}</MDTypography>
              </MDBox>
              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Submitted On:
                </MDTypography>
                <MDTypography variant="body2">
                  {new Date(selectedSubmission.createdAt).toLocaleString()}
                </MDTypography>
              </MDBox>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseDialog} color="info">
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      <PaymentLinkModal
        open={!!paymentLinkTarget}
        onClose={() => setPaymentLinkTarget(null)}
        prefill={{
          name: paymentLinkTarget?.name || "",
          email: paymentLinkTarget?.email || "",
          phone: paymentLinkTarget?.phone || "",
          description: `Contact enquiry – ${
            paymentLinkTarget?.subject || paymentLinkTarget?.name || ""
          }`,
        }}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default ContactSubmissions;
